import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../api';

function NewsPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const data = await api.getNewsPost(slug);
        setPost(data);
      } catch (err) {
        console.error('Failed to fetch post:', err);
        setError('Post not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 20 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Box sx={{ py: 20, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ mb: 3 }}>
            Post Not Found
          </Typography>
          <Button component={Link} to="/news" startIcon={<ArrowBackIcon />}>
            Back to News
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          pt: 16,
          pb: 8,
          background: 'linear-gradient(135deg, #1a237e 0%, #0d1b5e 100%)',
          color: '#fff',
        }}
      >
        <Container maxWidth="md">
          <Button
            component={Link}
            to="/news"
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'rgba(255,255,255,0.8)', mb: 4, '&:hover': { color: '#fff' } }}
          >
            Back to News
          </Button>
          <Typography
            variant="caption"
            sx={{ color: 'secondary.main', fontWeight: 600, display: 'block', mb: 2 }}
          >
            {new Date(post.publishedAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
            {post.title}
          </Typography>
        </Container>
      </Box>

      {/* Featured Image - Full Width */}
      {post.imageUrl && (
        <Box
          sx={{
            width: '100%',
            maxHeight: '70vh',
            overflow: 'hidden',
            bgcolor: '#000',
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 0, md: 3 } }}>
            <Box
              component="img"
              src={post.imageUrl}
              alt={post.title}
              sx={{
                width: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Container>
        </Box>
      )}

      {/* YouTube Video */}
      {post.videoUrl && (
        <Box sx={{ py: 4, bgcolor: '#000' }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                position: 'relative',
                paddingBottom: '56.25%', // 16:9 aspect ratio
                height: 0,
                overflow: 'hidden',
                borderRadius: 2,
              }}
            >
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${post.videoUrl.match(/(?:v=|youtu\.be\/)([^&\s]+)/)?.[1] || ''}`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                allowFullScreen
                title={post.title}
              />
            </Box>
          </Container>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.125rem',
              lineHeight: 1.9,
              color: 'text.secondary',
              '& p': { mb: 3 },
              '& h2, & h3': { 
                mt: 4, 
                mb: 2, 
                color: 'text.primary',
                fontFamily: "'Montserrat', sans-serif",
              },
              '& a': { color: 'primary.main' },
              '& img': { 
                maxWidth: '100%', 
                height: 'auto', 
                borderRadius: 2,
                my: 3,
              },
              '& ul, & ol': { pl: 3, mb: 3 },
              '& li': { mb: 1 },
            }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          <Divider sx={{ my: 6 }} />

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              component={Link}
              to="/news"
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
            >
              Back to All News
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default NewsPost;
